import { ColorPicker, Form, Tag, Tooltip } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { wcagLevel, bestContrastRatio, autoTextColor } from 'utils/colorContrast';

const HEX_REGEX = /^#[0-9a-fA-F]{6}$/;

// Campo de color con preview + indicador WCAG AA/AAA + bloqueo de submit si falla.
// Se usa dentro de <Form> de antd; el campo se registra por `name` y el Form
// maneja el valor como string hex '#RRGGBB'.
//
// Props:
//   - name: nombre del campo en el Form
//   - label: label visible
//   - help: texto secundario opcional debajo del campo
const ColorFieldWithWcag = ({ name, label, help }) => (
  <Form.Item
    label={label}
    extra={help}
    shouldUpdate={(prev, curr) => prev?.[name] !== curr?.[name]}
  >
    {({ getFieldValue, setFieldValue }) => {
      const value = getFieldValue(name);
      const level = HEX_REGEX.test(value || '') ? wcagLevel(value) : null;
      const ratio = level !== null && level !== 'fail' ? bestContrastRatio(value) : null;
      const failRatio = level === 'fail' ? bestContrastRatio(value) : null;
      const textColor = HEX_REGEX.test(value || '') ? autoTextColor(value) : '#000000';

      return (
        <>
          {/* Input oculto del Form + reglas */}
          <Form.Item
            name={name}
            noStyle
            rules={[
              { required: true, message: 'Color requerido' },
              {
                pattern: HEX_REGEX,
                message: 'Formato hex inválido (#RRGGBB)',
              },
              {
                validator: (_, v) =>
                  !v || wcagLevel(v) !== 'fail'
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error(
                          `Contraste insuficiente (${bestContrastRatio(v).toFixed(
                            1
                          )}:1, WCAG AA requiere 4.5:1)`
                        )
                      ),
              },
            ]}
          >
            <input type="hidden" />
          </Form.Item>

          <div className="flex items-center gap-2 flex-wrap">
            <ColorPicker
              format="hex"
              value={value}
              onChange={(c) => setFieldValue(name, c.toHexString())}
              showText
              size="default"
            />

            {level === 'aaa' && (
              <Tag color="green" icon={<CheckCircleOutlined />}>
                AAA · {ratio.toFixed(1)}
              </Tag>
            )}
            {level === 'aa' && (
              <Tag color="blue" icon={<CheckCircleOutlined />}>
                AA · {ratio.toFixed(1)}
              </Tag>
            )}
            {level === 'fail' && (
              <Tooltip title="Elige un color con más contraste (el texto auto será blanco o negro según cuál lea mejor).">
                <Tag color="red" icon={<CloseCircleOutlined />}>
                  Falla · {failRatio.toFixed(1)}
                </Tag>
              </Tooltip>
            )}

            {HEX_REGEX.test(value || '') && (
              <span
                className="px-2 py-1 rounded text-xs font-medium"
                style={{ backgroundColor: value, color: textColor }}
              >
                Texto demo
              </span>
            )}
          </div>
        </>
      );
    }}
  </Form.Item>
);

export default ColorFieldWithWcag;
